import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import DataTable, { Column } from '@repo/ui/data-table'
import { PlusIcon, TravelTwoIcon } from '@repo/ui/icons'
import Modal from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useFormContext } from 'react-hook-form'

import {
  getCountriesList,
  getCountriesListQueryKey
} from '../../../api/countries/get-countries-list'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { ProductTypeEnum } from '../../../lib/types/enums/product/product-type.enum'
import { CenterInput } from '../../../lib/types/models/center/center-input'
import { CenterProperties } from '../../../lib/types/models/center/properties.center'
import { CenterToJourneyInput } from '../../../lib/types/models/center-to-journey/center-to-journey-input'
import { CenterToJourneyProperties } from '../../../lib/types/models/center-to-journey/properties.center-to-journey'
import { IdInput } from '../../../lib/types/utils'
import ActionTableWrapper from '../../elements/action-table-wrapper'
import CenterJourneySelectionList from './assign-journey-to-center/center-journey-selection-list'

const CenterJourneys = () => {
  const [step, setStep] = useState(0)

  const form = useFormContext<CenterInput>()

  const { modalState, toggleModal } = useModalManager<'add'>(['add'])

  const { data: countryList } = useQuery({
    queryKey: getCountriesListQueryKey(),
    queryFn: () => getCountriesList()
  })

  const columns: Column<IdInput<CenterToJourneyInput>>[] = [
    {
      accessorKey: 'journey.date.product.country.name',
      header: 'Pays',
      enableSorting: true,
      cell: ({ row }) => (
        <div className={'flex items-center gap-2 w-20'}>
          <img
            src={countryList?.mapping[CenterToJourneyProperties.getCountryCode(row.original)]}
            alt={'flag'}
            className={'w-4 h-4 rounded-full'}
          />
        </div>
      )
    },
    {
      accessorKey: 'journey.date',
      header: 'Dates du séjour',
      cell: ({ row }) => <Badge text={CenterToJourneyProperties.getTripDate(row.original)} />
    },
    {
      accessorKey: 'journey.date.product.formula.title',
      header: 'Formule',
      enableSorting: true,
      cell: ({ row }) => CenterToJourneyProperties.getFormulaName(row.original)
    },
    {
      accessorKey: 'journey.mainOption.title',
      header: 'Thème',
      enableSorting: true,
      cell: ({ row }) => CenterToJourneyProperties.getThemeName(row.original)
    },
    {
      accessorKey: 'journey.secondaryOptions',
      header: 'Option(s) facultative(s)',
      cell: ({ row }) => (
        <div className="flex flex-wrap">
          {CenterToJourneyProperties.getOptions(row.original).map((option) => (
            <Typography key={option} variant="secondary-body" className="mr-2">
              {option}
            </Typography>
          ))}
        </div>
      )
    },
    {
      accessorKey: 'journey.date',
      header: 'Voyages principaux',
      cell: ({ row }) => {
        const mainTrips = CenterToJourneyProperties.getMainTrips(row.original)
        return (
          <div className="flex flex-wrap">
            {mainTrips.map((trip) => (
              <Typography key={trip} variant="secondary-body" className="mr-2">
                {trip}
              </Typography>
            ))}
          </div>
        )
      }
    },
    {
      accessorKey: 'id',
      header: 'Voyages secondaires',
      enableSorting: true,
      cell: ({ row }) => {
        const secondaryTrips = CenterToJourneyProperties.getSecondaryTrips(row.original)
        return (
          <div className="flex flex-wrap">
            {secondaryTrips.map((trip) => (
              <Typography key={trip} variant="secondary-body" className="mr-2">
                {trip}
              </Typography>
            ))}
          </div>
        )
      }
    },
    {
      accessorKey: 'date.product.numberOfHoursPerWeek',
      header: "# d'h de cours / sem",
      cell: ({ row }) =>
        row.original.journey.date.product.type === ProductTypeEnum.ONE_TO_ONE
          ? (row.original.journey.date.product.numberOfHoursPerWeek ?? '-')
          : '-'
    },
    {
      accessorKey: 'maxNumberOfStudentPlaces',
      header: 'Nombre de places',
      enableSorting: true
    }
  ]

  const assignJourneyToCenter = form.watch('assignJourneyToCenter')

  const centerToJourneys = assignJourneyToCenter.centerToJourneys.map((j) => ({
    ...j,
    id: j.journey.id
  }))

  return (
    <ActionTableWrapper
      title={'Séjours assignés'}
      titleColor={tailwindColors.black.DEFAULT}
      actions={[
        <Button
          key={'add-journey'}
          type="button"
          icon={PlusIcon}
          label={'Assigner un séjour'}
          variant={'outline'}
          onClick={() => {
            toggleModal('add')
          }}
        />
      ]}
    >
      <DataTable<IdInput<CenterToJourneyInput>>
        data={centerToJourneys}
        columns={columns}
        rowCount={centerToJourneys.length}
        paginated={false}
      />

      <Modal
        isOpen={modalState.add}
        title={CenterProperties.getModalTitleBasedOnStep(step)}
        titleAlignment="left"
        size={step === 0 ? 'large' : 'medium'}
        hideActions
        icon={TravelTwoIcon}
        onCancel={() => {
          toggleModal('add')
          setStep(0)
        }}
      >
        <CenterJourneySelectionList
          step={step}
          onStepChange={setStep}
          defaultValues={form.watch('assignJourneyToCenter')}
          onSubmit={(values) => {
            form.setValue('assignJourneyToCenter', values)
            setStep(0)
            toggleModal('add')
          }}
        />
      </Modal>
    </ActionTableWrapper>
  )
}

export default CenterJourneys
